import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Play,
  CheckCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  Edit3,
  Trash2,
  FileText,
  ArrowLeft,
} from "lucide-react";
import { ocrApi } from "../../../services/ocrApi";

// Types
interface ExtractedData {
  lineItems: Record<string, unknown[]>;
  fieldItems: Record<string, Record<string, unknown>>;
  processedLineItems: Record<string, unknown[]>;
  processedFieldItems: Record<string, Record<string, unknown>>;
  totalPages: number;
}

interface NavigationState {
  process_id?: string;
  layout_id?: string;
  workflow_id?: string;
}

type TabType = "automatic" | "semi-automatic" | "manual";
type SubTabType = "lineItems" | "fieldItems";

// Tab configurations
// "Automatic" tab is intentionally hidden — product wants reviewers to
// start with Semi-automatic. The TabType still includes "automatic" and
// the prompt-execution handler is preserved (so the codepath stays
// usable if the tab is added back), but it doesn't appear in the
// visible tab strip below.
const TABS: { label: string; value: TabType }[] = [
  { label: "Semi-automatic", value: "semi-automatic" },
  { label: "Manual", value: "manual" },
];

const COMPILERS = [
  { label: "Python", value: "python" },
  { label: "JavaScript", value: "javascript" },
];

export function BusinessRulesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Navigation state from location or URL params
  const navState = (location.state as NavigationState) || {};
  const processId = navState.process_id || searchParams.get("id") || "";
  const layoutId = navState.layout_id || searchParams.get("layout") || "";
  const workflowId = navState.workflow_id || searchParams.get("workflow") || "";
  // `table_name` is forwarded by the cockpit / detail panel so this
  // page roundtrips back to the right inbox.
  const tableNameParam =
    searchParams.get("table_name") || "doc_extraction_tracking";
  const inboxPath =
    tableNameParam === "config_studio_tracking"
      ? "/studio/templateEngine"
      : "/ocr-tracking";

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>("semi-automatic");
  const [subTab, setSubTab] = useState<SubTabType>("lineItems");
  const [currentPage, setCurrentPage] = useState(1);

  // Form state
  const [lineItemPrompt, setLineItemPrompt] = useState("");
  const [fieldItemPrompt, setFieldItemPrompt] = useState("");
  const [script, setScript] = useState("");
  const [compiler, setCompiler] = useState("python");

  // Raw business-config response from `business_configuration/home_page`.
  // We need the original page metadata (image_path, table_name,
  // column_boundary, total_width) to rebuild the `expected_output`
  // payload for /generate_script — the backend's script generator
  // rejects bare {table_rows, fields} payloads. See handleProcess for
  // the shape it expects.
  const businessConfigRef = useRef<Record<string, unknown> | null>(null);

  // Data state
  const [extractedData, setExtractedData] = useState<ExtractedData>({
    lineItems: {},
    fieldItems: {},
    processedLineItems: {},
    processedFieldItems: {},
    totalPages: 0,
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Selection state for delete functionality
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Semi-automatic specific states
  const [isDownloadMode, setIsDownloadMode] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Fetch initial data
  const fetchBusinessConfig = useCallback(async () => {
    if (!processId) return;
    setIsLoading(true);
    try {
      const response = await ocrApi.fetchBusinessConfiguration({
        processId,
        tableName: layoutId || "config_studio_tracking",
      });

      const data = response as Record<string, unknown>;
      // Stash the raw response — handleProcess needs the original page
      // metadata (image_path, table_name, column_boundary, total_width)
      // to build the script-generator's expected_output payload.
      businessConfigRef.current = data;
      const lineItems: Record<string, unknown[]> = {};
      const fieldItems: Record<string, Record<string, unknown>> = {};
      let totalPages = 0;

      // Parse line items
      const lineItemContent = (data.lineitem_extracted_content || data.line_item_data || {}) as Record<string, unknown>;
      totalPages = (lineItemContent.total_pages as number) || totalPages;

      Object.entries(lineItemContent).forEach(([page, pageData]) => {
        if (page === "total_pages" || page === "process_id") return;
        if (!isNaN(Number(page))) {
          const tables = (pageData as Record<string, unknown>)?.extracted_content as Record<string, unknown>;
          const tablesArr = (tables?.tables as unknown[]) || [];
          if (tablesArr.length > 0) {
            const rows = ((tablesArr[0] as Record<string, unknown>)?.table_rows as unknown[]) || [];
            if (rows.length > 0) lineItems[page] = rows;
          }
        }
      });

      // Parse field items
      const fieldItemContent = (data.fielditem_extracted_content || data.field_item_data || {}) as Record<string, unknown>;
      totalPages = totalPages || (fieldItemContent.total_pages as number) || 0;

      Object.entries(fieldItemContent).forEach(([page, pageData]) => {
        if (page === "total_pages" || page === "process_id") return;
        if (!isNaN(Number(page))) {
          const extractedContent = (pageData as Record<string, unknown>)?.extracted_content;
          const contentArr = Array.isArray(extractedContent) ? extractedContent : [];
          const flattenedFields: Record<string, unknown> = {};

          contentArr.forEach((group: Record<string, unknown>) => {
            if (group.fields && Array.isArray(group.fields)) {
              (group.fields as Record<string, unknown>[]).forEach((fieldWrapper) => {
                Object.entries(fieldWrapper).forEach(([fieldName, fieldData]) => {
                  const fd = fieldData as Record<string, unknown>;
                  flattenedFields[fieldName] = fd?.value !== undefined ? fd.value : fieldData;
                });
              });
            }
          });

          if (Object.keys(flattenedFields).length > 0) {
            fieldItems[page] = flattenedFields;
          }
        }
      });

      if (!totalPages) {
        totalPages = Math.max(Object.keys(lineItems).length, Object.keys(fieldItems).length, 1);
      }

      setExtractedData((prev) => ({ ...prev, lineItems, fieldItems, totalPages }));
    } catch (error) {
      console.error("Error fetching business config:", error);
      toast.error("Failed to load business configuration");
    } finally {
      setIsLoading(false);
    }
  }, [processId, layoutId]);

  useEffect(() => {
    fetchBusinessConfig();
  }, [fetchBusinessConfig]);

  // Execute prompt (Automatic tab)
  const handleExecutePrompt = async () => {
    if (!processId || !layoutId) {
      toast.error("Missing process or layout ID");
      return;
    }
    setIsExecuting(true);
    try {
      const response = await ocrApi.executePrompt({
        processId,
        layoutId,
        lineItemPrompt,
        fieldItemPrompt,
      });

      const data = response as Record<string, unknown>;
      const processedLineItems: Record<string, unknown[]> = {};
      const processedFieldItems: Record<string, Record<string, unknown>> = {};

      // Parse processed line items
      const lineItemContent = (data.lineitem_content || data.lineitem_extracted_content || {}) as Record<string, unknown>;
      Object.entries(lineItemContent).forEach(([page, pageData]) => {
        if (page === "total_pages" || page === "process_id") return;
        if (!isNaN(Number(page))) {
          const tables = (pageData as Record<string, unknown>)?.extracted_content as Record<string, unknown>;
          const tablesArr = (tables?.tables as unknown[]) || [];
          if (tablesArr.length > 0) {
            const rows = ((tablesArr[0] as Record<string, unknown>)?.table_rows as unknown[]) || [];
            if (rows.length > 0) processedLineItems[page] = rows;
          }
        }
      });

      // Parse processed field items
      const fieldItemContent = (data.fielditem_content || data.fielditem_extracted_content || {}) as Record<string, unknown>;
      Object.entries(fieldItemContent).forEach(([page, pageData]) => {
        if (page === "total_pages" || page === "process_id") return;
        if (!isNaN(Number(page))) {
          const extractedContent = (pageData as Record<string, unknown>)?.extracted_content;
          const contentArr = Array.isArray(extractedContent) ? extractedContent : [];
          const flattenedFields: Record<string, unknown> = {};

          contentArr.forEach((group: Record<string, unknown>) => {
            if (group.fields && Array.isArray(group.fields)) {
              (group.fields as Record<string, unknown>[]).forEach((fieldWrapper) => {
                Object.entries(fieldWrapper).forEach(([fieldName, fieldData]) => {
                  const fd = fieldData as Record<string, unknown>;
                  flattenedFields[fieldName] = fd?.value !== undefined ? fd.value : fieldData;
                });
              });
            }
          });

          if (Object.keys(flattenedFields).length > 0) {
            processedFieldItems[page] = flattenedFields;
          }
        }
      });

      setExtractedData((prev) => ({
        ...prev,
        processedLineItems,
        processedFieldItems,
      }));

      toast.success("Prompt executed successfully");
    } catch (error) {
      console.error("Error executing prompt:", error);
      toast.error("Failed to execute prompt");
    } finally {
      setIsExecuting(false);
    }
  };

  // Execute script (Manual tab)
  const handleExecuteScript = async () => {
    if (!script.trim()) {
      toast.warning("Please enter a script before executing");
      return;
    }
    if (!processId || !layoutId) {
      toast.error("Missing process or layout ID");
      return;
    }
    setIsExecuting(true);
    try {
      const response = await ocrApi.executeScript({
        processId,
        layoutId,
        script,
        scriptType: compiler,
        businessRulesType: "automatic",
      });

      const data = response as Record<string, unknown>;
      const processedLineItems: Record<string, unknown[]> = {};
      const processedFieldItems: Record<string, Record<string, unknown>> = {};

      // Parse response similar to executePrompt
      const lineItemContent = (data.lineitem_extracted_content || {}) as Record<string, unknown>;
      Object.entries(lineItemContent).forEach(([page, pageData]) => {
        if (!isNaN(Number(page))) {
          const tables = (pageData as Record<string, unknown>)?.extracted_content as Record<string, unknown>;
          const tablesArr = (tables?.tables as unknown[]) || [];
          if (tablesArr.length > 0) {
            const rows = ((tablesArr[0] as Record<string, unknown>)?.table_rows as unknown[]) || [];
            if (rows.length > 0) processedLineItems[page] = rows;
          }
        }
      });

      const fieldItemContent = (data.fielditem_extracted_content || {}) as Record<string, unknown>;
      Object.entries(fieldItemContent).forEach(([page, pageData]) => {
        if (!isNaN(Number(page))) {
          const extractedContent = (pageData as Record<string, unknown>)?.extracted_content;
          if (Array.isArray(extractedContent)) {
            const flattenedFields: Record<string, unknown> = {};
            extractedContent.forEach((group: Record<string, unknown>) => {
              if (group.fields && Array.isArray(group.fields)) {
                (group.fields as Record<string, unknown>[]).forEach((fieldWrapper) => {
                  Object.entries(fieldWrapper).forEach(([fieldName, fieldData]) => {
                    const fd = fieldData as Record<string, unknown>;
                    flattenedFields[fieldName] = fd?.value !== undefined ? fd.value : fieldData;
                  });
                });
              }
            });
            if (Object.keys(flattenedFields).length > 0) {
              processedFieldItems[page] = flattenedFields;
            }
          }
        }
      });

      setExtractedData((prev) => ({
        ...prev,
        processedLineItems,
        processedFieldItems,
      }));

      toast.success("Script executed successfully");
    } catch (error) {
      console.error("Error executing script:", error);
      toast.error("Failed to execute script");
    } finally {
      setIsExecuting(false);
    }
  };

  // Process (Semi-automatic tab)
  const handleProcess = async () => {
    if (!processId || !layoutId) {
      toast.error("Missing process or layout ID");
      return;
    }
    setIsExecuting(true);
    try {
      // Build expected_output payload — mirrors ocr-ui's
      // SemiAutomaticTab.handleGenerateScript exactly. The script
      // generator backend needs the original page metadata
      // (image_path, table_name, column_boundary, total_width) attached
      // alongside the user-edited rows; without these the
      // /generate_script call fails because its template-builder can't
      // locate the source layout.
      const cfg = businessConfigRef.current ?? {};
      const lineConfig = (cfg.lineitem_extracted_content ||
        (cfg as Record<string, unknown>).line_item_data ||
        {}) as Record<string, unknown>;
      const fieldConfig = (cfg.fielditem_extracted_content ||
        (cfg as Record<string, unknown>).field_item_data ||
        {}) as Record<string, unknown>;

      const exportData: Record<string, unknown> = { process_id: processId };
      const allPages = new Set([
        ...Object.keys(lineConfig),
        ...Object.keys(fieldConfig),
        ...Object.keys(extractedData.lineItems),
        ...Object.keys(extractedData.fieldItems),
      ]);

      allPages.forEach((page) => {
        if (page === "total_pages" || page === "process_id") return;
        if (isNaN(Number(page))) return;

        const linePageData =
          (lineConfig[page] as Record<string, unknown> | undefined) ?? {};
        const fieldPageData =
          (fieldConfig[page] as Record<string, unknown> | undefined) ?? {};

        const lineExtracted =
          (linePageData.extracted_content as Record<string, unknown> | undefined) ?? {};
        const tablesArr = (lineExtracted.tables as unknown[]) ?? [];
        const originalTable =
          (tablesArr[0] as Record<string, unknown> | undefined) ?? {};

        const currentRows = extractedData.lineItems[page] || [];

        exportData[page] = {
          image_path:
            (linePageData.image_path as string | undefined) ||
            (fieldPageData.image_path as string | undefined) ||
            "",
          extracted_content: {
            lineitem_content: [
              {
                table_name: (originalTable.table_name as string) || "table_1",
                table_rows: currentRows,
                column_boundary:
                  (originalTable.column_boundary as Record<string, unknown>) ||
                  {},
                total_width: (originalTable.total_width as number) || 0,
              },
            ],
            fielditem_content: [
              { fields: extractedData.fieldItems[page] || {} },
            ],
          },
        };
      });

      const jsonString = JSON.stringify(exportData);
      const expectedOutput = btoa(unescape(encodeURIComponent(jsonString)));

      // Generate script
      const genResponse = await ocrApi.generateScript({
        processId,
        layoutId,
        expectedOutput,
      });

      const generatedScript = (genResponse as Record<string, string>)?.manual_python_script ||
        (genResponse as Record<string, string>)?.script || "";

      if (generatedScript) {
        setScript(generatedScript);

        // Execute the generated script. The semi-automatic flow REQUIRES
        // expected_output + expected_output_file_name alongside the
        // script — without them the backend pydantic model rejects the
        // call with 422. Mirrors ocr-ui's SemiAutomaticTab which spreads
        // the same payload it sent to generateScript.
        const execResponse = await ocrApi.executeScript({
          processId,
          layoutId,
          script: generatedScript,
          scriptType: "python",
          businessRulesType: "TV",
          expectedOutput,
          expectedOutputFileName: "extracted_data.json",
        });

        // Parse response and update processed data. Mirrors the
        // reference's SemiAutomaticTab handler — it reads both
        // `lineitem_extracted_content` (table rows) AND
        // `fielditem_extracted_content` (flattened {fieldName: value}
        // map), with the same shape-tolerance for the wrapping
        // `extracted_content` field. Our previous version only parsed
        // line items, so the Field Items tab's "Processed Output"
        // panel was always empty.
        const data = execResponse as Record<string, unknown>;
        // eslint-disable-next-line no-console
        console.log("[BusinessRules.handleProcess] execute_script response", data);
        const processedLineItems: Record<string, unknown[]> = {};
        const processedFieldItems: Record<string, Record<string, unknown>> = {};

        const lineItemContent = (data.lineitem_extracted_content ||
          data.lineitem_content ||
          {}) as Record<string, unknown>;
        Object.entries(lineItemContent).forEach(([page, pageData]) => {
          if (page === "total_pages" || page === "process_id") return;
          if (!isNaN(Number(page))) {
            const tables = (pageData as Record<string, unknown>)?.extracted_content as Record<string, unknown>;
            const tablesArr = (tables?.tables as unknown[]) || [];
            if (tablesArr.length > 0) {
              const rows = ((tablesArr[0] as Record<string, unknown>)?.table_rows as unknown[]) || [];
              if (rows.length > 0) processedLineItems[page] = rows;
            }
          }
        });

        const fieldItemContent = (data.fielditem_extracted_content ||
          data.fielditem_content ||
          {}) as Record<string, unknown>;
        Object.entries(fieldItemContent).forEach(([page, pageData]) => {
          if (page === "total_pages" || page === "process_id") return;
          if (!isNaN(Number(page))) {
            const extractedContent = (pageData as Record<string, unknown>)?.extracted_content;
            const groups = Array.isArray(extractedContent)
              ? extractedContent
              : extractedContent
                ? [extractedContent]
                : [];
            const flattenedFields: Record<string, unknown> = {};
            (groups as Record<string, unknown>[]).forEach((group) => {
              const fields = group?.fields;
              if (Array.isArray(fields)) {
                (fields as Record<string, unknown>[]).forEach((fieldWrapper) => {
                  Object.entries(fieldWrapper).forEach(([fieldName, fieldData]) => {
                    const fd = fieldData as Record<string, unknown>;
                    flattenedFields[fieldName] =
                      fd?.value !== undefined ? fd.value : fieldData;
                  });
                });
              } else if (fields && typeof fields === "object") {
                Object.entries(fields as Record<string, unknown>).forEach(
                  ([fieldName, fieldData]) => {
                    const fd = fieldData as Record<string, unknown>;
                    flattenedFields[fieldName] =
                      fd?.value !== undefined ? fd.value : fieldData;
                  },
                );
              }
            });
            if (Object.keys(flattenedFields).length > 0) {
              processedFieldItems[page] = flattenedFields;
            }
          }
        });

        setExtractedData((prev) => ({
          ...prev,
          processedLineItems: {
            ...prev.processedLineItems,
            ...processedLineItems,
          },
          processedFieldItems: {
            ...prev.processedFieldItems,
            ...processedFieldItems,
          },
        }));
      }

      toast.success("Processing completed successfully");
    } catch (error) {
      console.error("Error processing:", error);
      toast.error("Failed to process");
    } finally {
      setIsExecuting(false);
    }
  };

  // Certify and save
  const handleCertify = async () => {
    if (!processId || !layoutId) {
      toast.error("Missing process or layout ID");
      return;
    }
    setIsSubmitting(true);
    try {
      // Save business rules
      await ocrApi.saveBusinessRules({
        processId,
        layoutId,
        businessRulesType: activeTab === "automatic" ? "prompt" : "automatic",
        lineItemPrompt: activeTab === "automatic" ? lineItemPrompt : undefined,
        fieldItemPrompt: activeTab === "automatic" ? fieldItemPrompt : undefined,
        script: activeTab !== "automatic" ? script : undefined,
        scriptType: compiler,
        saveRules: true,
      });

      // Submit verification. `table_name` is forwarded from the URL so
      // the backend marks the right tracking collection — the cockpit
      // / detail-panel hand-off threads `config_studio_tracking` through
      // for Template Engine docs and `doc_extraction_tracking` for OCR
      // Tracking docs.
      await ocrApi.verificationSubmit(processId, {
        workflow_id: workflowId,
        verification_component: "BC",
        table_name: tableNameParam,
      });

      toast.success("Business rules certified successfully!");
      navigate(inboxPath);
    } catch (error) {
      console.error("Error certifying:", error);
      toast.error("Failed to certify business rules");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate(inboxPath);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Create a JSON blob of the current data
      const exportData = {
        line_items: extractedData.lineItems,
        field_items: extractedData.fieldItems,
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `business_rules_${processId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (error) {
      console.error("Error downloading:", error);
      toast.error("Failed to download");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      // Parse uploaded data and update extractedData
      if (jsonData.line_items) {
        const lineItems: Record<string, unknown[]> = {};
        Object.entries(jsonData.line_items).forEach(([page, data]) => {
          const pageNum = page.replace("page_", "");
          if (Array.isArray(data)) {
            lineItems[pageNum] = data;
          }
        });
        setExtractedData(prev => ({ ...prev, lineItems }));
      }

      if (jsonData.field_items) {
        const fieldItems: Record<string, Record<string, unknown>> = {};
        Object.entries(jsonData.field_items).forEach(([page, data]) => {
          const pageNum = page.replace("page_", "");
          if (typeof data === "object" && data !== null) {
            fieldItems[pageNum] = data as Record<string, unknown>;
          }
        });
        setExtractedData(prev => ({ ...prev, fieldItems }));
      }

      toast.success(`File "${file.name}" uploaded and parsed successfully`);
    } catch (error) {
      console.error("Error parsing file:", error);
      toast.error("Failed to parse uploaded file. Please ensure it's a valid JSON.");
    }
  };

  // Handle raw data change for edit mode
  const handleRawDataChange = (
    type: "lineItem" | "fieldItem",
    page: number,
    idx: number | null,
    key: string,
    value: string
  ) => {
    setExtractedData((prevData) => {
      const newData = { ...prevData };

      if (type === "lineItem" && idx !== null) {
        const newPageItems = [...(newData.lineItems[page] || [])];
        if (newPageItems[idx] && typeof newPageItems[idx] === "object") {
          const row = { ...(newPageItems[idx] as Record<string, unknown>) };
          if (typeof row[key] === "object" && row[key] !== null) {
            row[key] = { ...(row[key] as Record<string, unknown>), value };
          } else {
            row[key] = value;
          }
          newPageItems[idx] = row;
        }
        newData.lineItems = { ...newData.lineItems, [page]: newPageItems };
      } else if (type === "fieldItem") {
        const newPageItems = { ...(newData.fieldItems[page] || {}) };
        if (typeof newPageItems[key] === "object" && newPageItems[key] !== null) {
          newPageItems[key] = { ...(newPageItems[key] as Record<string, unknown>), value };
        } else {
          newPageItems[key] = value;
        }
        newData.fieldItems = { ...newData.fieldItems, [page]: newPageItems };
      }

      return newData;
    });
  };

  // Handle row selection
  const handleSelectRow = (index: number) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Handle select all rows
  const handleSelectAllRows = (data: unknown[]) => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(data.map((_, idx) => idx));
    }
  };

  // Handle column selection
  const handleColumnSelect = (columnName: string) => {
    setSelectedColumns((prev) =>
      prev.includes(columnName) ? prev.filter((c) => c !== columnName) : [...prev, columnName]
    );
  };

  // Handle delete selected rows/columns
  const handleDeleteSelected = () => {
    if (selectedRows.length === 0 && selectedColumns.length === 0) return;

    setExtractedData((prev) => {
      const currentData = prev.lineItems[currentPage] || [];
      let newData = [...currentData] as Record<string, unknown>[];

      // Delete selected rows
      if (selectedRows.length > 0) {
        newData = newData.filter((_, idx) => !selectedRows.includes(idx));
      }

      // Delete selected columns
      if (selectedColumns.length > 0) {
        newData = newData.map((row) => {
          const newRow = { ...row };
          selectedColumns.forEach((col) => delete newRow[col]);
          return newRow;
        });
      }

      return {
        ...prev,
        lineItems: { ...prev.lineItems, [currentPage]: newData },
      };
    });

    setSelectedRows([]);
    setSelectedColumns([]);
    setShowDeleteConfirm(false);
    toast.success("Selected items deleted");
  };

  // Render editable line items table
  const renderLineItemsTable = (
    data: unknown[],
    title: string,
    isRawOutput: boolean = false,
    emptyMessage?: string
  ) => {
    // For Semi-automatic tab: edit when not in download mode
    // For other tabs: always allow editing on raw output
    const canEdit = isRawOutput && (activeTab === "semi-automatic" ? !isDownloadMode : true);
    const hasSelections = selectedRows.length > 0 || selectedColumns.length > 0;

    if (!data || data.length === 0) {
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <h4 className="text-base font-semibold text-blue-600">{title}</h4>
          </div>
          <div className="flex flex-1 items-center justify-center p-8">
            <p className="text-sm text-gray-400">{emptyMessage || "No data available"}</p>
          </div>
        </div>
      );
    }

    const headers = Object.keys(data[0] as Record<string, unknown>);

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <h4 className="text-base font-semibold text-blue-600">{title}</h4>
            {canEdit && hasSelections && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-600 ring-1 ring-inset ring-red-200">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                {selectedRows.length > 0 && `${selectedRows.length} Row(s)`}
                {selectedRows.length > 0 && selectedColumns.length > 0 && " & "}
                {selectedColumns.length > 0 && `${selectedColumns.length} Column(s)`}
                {" Selected"}
              </span>
            )}
          </div>
          {canEdit && hasSelections && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="min-w-full">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                {canEdit && (
                  <th className="w-10 border-b border-gray-200 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={data.length > 0 && selectedRows.length === data.length}
                      onChange={() => handleSelectAllRows(data)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                {headers.map((header) => {
                  const isSelected = selectedColumns.includes(header);
                  return (
                    <th
                      key={header}
                      onClick={() => canEdit && handleColumnSelect(header)}
                      className={`whitespace-nowrap border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider transition-colors ${isSelected
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600"
                        } ${canEdit ? "cursor-pointer hover:bg-gray-100" : ""}`}
                    >
                      {header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((row, idx) => {
                const isRowSelected = selectedRows.includes(idx);
                return (
                  <tr
                    key={idx}
                    className={`transition-colors ${isRowSelected ? "bg-blue-50" : idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      } hover:bg-gray-100/50`}
                  >
                    {canEdit && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isRowSelected}
                          onChange={() => handleSelectRow(idx)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    )}
                    {headers.map((header) => {
                      const cellData = (row as Record<string, unknown>)[header];
                      const cellValue =
                        typeof cellData === "object" && cellData !== null
                          ? ((cellData as Record<string, unknown>).value as string) ?? ""
                          : String(cellData ?? "");
                      const isColSelected = selectedColumns.includes(header);

                      return (
                        <td
                          key={header}
                          className={`px-2 py-1.5 text-sm ${isColSelected ? "bg-blue-50" : ""
                            }`}
                        >
                          {canEdit ? (
                            <input
                              type="text"
                              value={cellValue}
                              onChange={(e) =>
                                handleRawDataChange("lineItem", currentPage, idx, header, e.target.value)
                              }
                              className="w-full min-w-[120px] rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                          ) : (
                            <span className="block px-3 py-2 text-gray-700">{cellValue}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render editable field items
  const renderFieldItems = (
    data: Record<string, unknown>,
    title: string,
    isRawOutput: boolean = false,
    emptyMessage?: string
  ) => {
    // For Semi-automatic tab: edit when not in download mode
    // For other tabs: always allow editing on raw output
    const canEdit = isRawOutput && (activeTab === "semi-automatic" ? !isDownloadMode : true);
    const entries = Object.entries(data || {});

    if (entries.length === 0) {
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <h4 className="text-base font-semibold text-blue-600">{title}</h4>
          </div>
          <div className="flex flex-1 items-center justify-center p-8">
            <p className="text-sm text-gray-400">{emptyMessage || "No data available"}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h4 className="text-base font-semibold text-blue-600">{title}</h4>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <div className="space-y-3">
            {entries.map(([key, value]) => {
              const displayValue =
                typeof value === "object" && value !== null
                  ? ((value as Record<string, unknown>).value as string) ?? ""
                  : String(value ?? "");
              return (
                <div
                  key={key}
                  className="flex items-center gap-4 rounded-lg bg-gray-50 p-3"
                >
                  <span className="min-w-[160px] text-sm font-medium text-gray-700">
                    {key}
                  </span>
                  {canEdit ? (
                    <input
                      type="text"
                      value={displayValue}
                      onChange={(e) =>
                        handleRawDataChange("fieldItem", currentPage, null, key, e.target.value)
                      }
                      className="flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  ) : (
                    <span className="flex-1 text-sm text-gray-600">{displayValue}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    if (activeTab === "automatic") {
      return (
        <div className="grid grid-cols-2 gap-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <label className="mb-3 block text-sm font-semibold text-gray-800">
              Line Item Prompt
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              rows={5}
              placeholder="Enter transformation prompt for line items..."
              value={lineItemPrompt}
              onChange={(e) => setLineItemPrompt(e.target.value)}
            />
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <label className="mb-3 block text-sm font-semibold text-gray-800">
              Field Item Prompt
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              rows={5}
              placeholder="Enter transformation prompt for field items..."
              value={fieldItemPrompt}
              onChange={(e) => setFieldItemPrompt(e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (activeTab === "semi-automatic") {
      return (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Mode:</span>
              <div className="flex items-center rounded-lg bg-gray-100 p-1">
                <button
                  onClick={() => setIsDownloadMode(false)}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${!isDownloadMode
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={() => setIsDownloadMode(true)}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${isDownloadMode
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <FileText className="h-4 w-4" />
                  Download/Upload
                </button>
              </div>
            </div>

            {isDownloadMode && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-100 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  {isDownloading ? "Downloading..." : "Download JSON"}
                </button>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-600 transition-colors hover:bg-green-100">
                  <Upload className="h-4 w-4" />
                  {uploadedFile ? uploadedFile.name : "Upload JSON"}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeTab === "manual") {
      return (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-800">Script Editor</label>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Language:</span>
              <select
                value={compiler}
                onChange={(e) => setCompiler(e.target.value)}
                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-medium focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {COMPILERS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <textarea
            className="w-full rounded-lg border border-gray-200 bg-gray-900 px-4 py-3 font-mono text-sm text-green-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            rows={8}
            placeholder="# Enter your transformation script here..."
            value={script}
            onChange={(e) => setScript(e.target.value)}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex h-full flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        {/* Top bar with title */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Business Rules Configuration</h1>
              <p className="text-xs text-gray-500">Process ID: {processId || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Main Tabs - Full width with underline */}
        <div className="flex border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setActiveTab(tab.value);
                setSelectedRows([]);
                setSelectedColumns([]);
              }}
              className={`relative flex-1 px-6 py-4 text-sm font-semibold transition-all ${activeTab === tab.value
                ? "text-blue-600 bg-blue-50/30"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
            >
              {tab.label}
              {activeTab === tab.value && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
              <span className="text-sm text-gray-500">Loading configuration...</span>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tab-specific content */}
            {renderTabContent()}

            {/* Output section */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              {/* Compact tabs header */}
              <div className="border-b border-gray-200">
                <div className="flex items-center justify-between px-4 py-3">
                  {/* Sub-tabs - Compact button style */}
                  <div className="inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1">
                    <button
                      onClick={() => {
                        setSubTab("lineItems");
                        setSelectedRows([]);
                        setSelectedColumns([]);
                      }}
                      className={`rounded-md px-4 py-1.5 text-xs font-semibold transition-all ${subTab === "lineItems"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      LINE ITEMS
                    </button>
                    <button
                      onClick={() => {
                        setSubTab("fieldItems");
                        setSelectedRows([]);
                        setSelectedColumns([]);
                      }}
                      className={`rounded-md px-4 py-1.5 text-xs font-semibold transition-all ${subTab === "fieldItems"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      FIELD ITEMS
                    </button>
                  </div>

                  {/* Pagination and Actions */}
                  <div className="flex items-center gap-4">
                    {/* Pagination */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Page</span>
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage <= 1}
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="inline-flex h-7 min-w-7 items-center justify-center rounded bg-blue-600 px-2 text-xs font-bold text-white">
                        {currentPage}
                      </span>
                      <span className="text-xs text-gray-400">/ {extractedData.totalPages || 1}</span>
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(extractedData.totalPages || 1, p + 1))}
                        disabled={currentPage >= (extractedData.totalPages || 1)}
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={
                          activeTab === "automatic"
                            ? handleExecutePrompt
                            : activeTab === "semi-automatic"
                              ? handleProcess
                              : handleExecuteScript
                        }
                        disabled={isExecuting}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Play className="h-3.5 w-3.5" />
                        {isExecuting
                          ? "Processing..."
                          : activeTab === "automatic"
                            ? "Execute Prompt"
                            : activeTab === "semi-automatic"
                              ? "Process"
                              : "Execute Script"}
                      </button>
                      <button
                        onClick={handleCertify}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-50"
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                        {isSubmitting ? "Certifying..." : "Certify Rule"}
                      </button>
                      <button
                        onClick={handleClose}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all hover:bg-gray-50"
                      >
                        <X className="h-3.5 w-3.5" />
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Output tables */}
              <div className="grid h-[400px] grid-cols-2 gap-4 p-5">
                {subTab === "lineItems" ? (
                  <>
                    {renderLineItemsTable(
                      extractedData.lineItems[currentPage] || [],
                      "Raw Output",
                      true
                    )}
                    {renderLineItemsTable(
                      extractedData.processedLineItems[currentPage] || [],
                      "Processed Output",
                      false,
                      "Execute to see processed output"
                    )}
                  </>
                ) : (
                  <>
                    {renderFieldItems(
                      extractedData.fieldItems[currentPage] || {},
                      "Raw Output",
                      true
                    )}
                    {renderFieldItems(
                      extractedData.processedFieldItems[currentPage] || {},
                      "Processed Output",
                      false,
                      "Execute to see processed output"
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Confirm Deletion</h3>
            <p className="mb-6 text-sm text-gray-600">
              Are you sure you want to delete
              {selectedRows.length > 0 && ` ${selectedRows.length} row(s)`}
              {selectedRows.length > 0 && selectedColumns.length > 0 && " and"}
              {selectedColumns.length > 0 && ` ${selectedColumns.length} column(s)`}?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSelected}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessRulesPage;
